import type { FC } from "react";
import { useSelector } from "@legendapp/state/react";
import { Column } from "primereact/column";
import { DataTable, type DataTablePageEvent } from "primereact/datatable";
import Avatar from "@/components/Avatar";
import {
  formatLastLogin,
  fullName,
  type ExternalUser,
  type UserRole,
} from "@/types/externalUser";
import { AuthStore$ } from "@/stores/authStore";
import { TEXT } from "@/constants/text";
import styles from "@/pages/admin/UserManagementPage/UsersTable.module.css";

export const PAGE_SIZE = 10;

const ROLE_LABEL: Record<UserRole, string> = {
  ADMIN: TEXT.userManagement.roleAdmin,
  MANAGER: TEXT.userManagement.roleManager,
  REPORTER: TEXT.userManagement.roleReporter,
};

const ROLE_CLASS: Record<UserRole, string> = {
  ADMIN: styles.roleAdmin,
  MANAGER: styles.roleManager,
  REPORTER: styles.roleReporter,
};

interface UsersTableProps {
  users: ExternalUser[];
  first: number;
  onPageChange: (first: number) => void;
  onEditUser: (user: ExternalUser) => void;
}

const UsersTable: FC<UsersTableProps> = ({
  users,
  first,
  onPageChange,
  onEditUser,
}) => {
  const session = useSelector(() => AuthStore$.session.get());

  const renderUserCell = (user: ExternalUser) => (
    <div className={styles.userCell}>
      <Avatar name={fullName(user)} variant="colored" />
      <div className={styles.userText}>
        <span className={styles.userName}>
          {fullName(user)}
          {session?.email.toLowerCase() === user.email.toLowerCase() && (
            <span className={styles.youSuffix}>
              {" "}
              {TEXT.userManagement.youSuffix}
            </span>
          )}
        </span>
        <span className={styles.userEmail}>{user.email}</span>
      </div>
    </div>
  );

  // Empty when T1 had nothing to say about this user — rendered blank rather
  // than guessing at a role, the same way a deactivated agency renders blank.
  const renderRoleCell = (user: ExternalUser) =>
    user.role === "" ? null : (
      <span className={`${styles.badge} ${ROLE_CLASS[user.role]}`}>
        {ROLE_LABEL[user.role]}
      </span>
    );

  const renderIdCell = (user: ExternalUser) => (
    <span className={styles.userId}>{user.id}</span>
  );

  const renderActionsCell = (user: ExternalUser) => (
    <div className={styles.actions}>
      <button
        className={styles.editButton}
        type="button"
        onClick={() => onEditUser(user)}
      >
        {TEXT.userManagement.editButton}
      </button>
    </div>
  );

  const shownCount = Math.min(PAGE_SIZE, Math.max(users.length - first, 0));
  const showingCount = (
    <span className={styles.showingCount}>
      {TEXT.userManagement.showingCountLabel
        .replace("{0}", String(shownCount))
        .replace("{1}", String(users.length))}
    </span>
  );

  return (
    <DataTable
      className={styles.table}
      dataKey="id"
      first={first}
      paginator
      paginatorLeft={showingCount}
      paginatorTemplate="PrevPageLink PageLinks NextPageLink"
      pt={{
        header: { className: styles.tableHeader },
        bodyRow: { className: styles.tableRow },
      }}
      rows={PAGE_SIZE}
      value={users}
      onPage={(event: DataTablePageEvent) => onPageChange(event.first)}
    >
      <Column
        body={renderIdCell}
        header={TEXT.userManagement.columnId}
        style={{ width: "8%" }}
      />
      <Column body={renderUserCell} header={TEXT.userManagement.columnUser} />
      <Column body={renderRoleCell} header={TEXT.userManagement.columnRole} />
      <Column
        body={(user: ExternalUser) => formatLastLogin(user.lastLoginOn)}
        header={TEXT.userManagement.columnLastLogin}
      />
      <Column body={renderActionsCell} />
    </DataTable>
  );
};

export default UsersTable;
